import React, { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';

import { authActions, userActions, applicationActions } from '../../../redux/actions';
import { languages } from '../../../config/maps';

import IconWithLabel from '../../../components/Icons/IconWithLabel';
import Box from '../../../components/Styled/Box';
import Typography from '../../../components/Typography/Typography';

const useHeaderMenuDropdown = ({
  companies,
  currentCompany,
  isAuthenticated,
  isManagerView,
  isAssessmentPage,
  userIsOwner,
  routes,
}) => {
  const { formatMessage: f } = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const chooseIconColor = useCallback((itemName) => {
    if (!isManagerView || !currentCompany) {
      return 'coldgrey500';
    }

    return (currentCompany.name === itemName) ? 'teal' : 'coldgrey500';
  },
  [currentCompany, isManagerView]);

  const onSelectingCompany = useCallback((company) => {
    dispatch(userActions.setCurrentCompany(company));
    // usuário sera direcionado para tabela inicial de manager

    // se o usuário esta na visão de respondente
    if (!isManagerView) {
      return dispatch(applicationActions.toggleManagerView());
    }

    // se o usuário nao esta nas tabelas de avaliação
    if (!isAssessmentPage) {
      return history.push(routes.manager.organizationalAssessments);
    }

    // nada acontece se esta navegando entre empresas com viewMode "manager"
    return null;
  }, [
    dispatch,
    history,
    isAssessmentPage,
    isManagerView,
    routes.manager.organizationalAssessments,
  ]);

  const companiesMenu = useMemo(() => [
    companies.map((d) => ({
      ...d,
      onClick: () => onSelectingCompany(d),
      label: (
        <IconWithLabel
          icon='industry-alt'
          typographyProps={{ variant: 'helper' }}
          iconProps={{ color: chooseIconColor(d.name) }}
        >
          {d.name}
        </IconWithLabel>
      ),
    })),
    [
      {
        id: 'all-companies',
        onClick: () => onSelectingCompany(null),
        label: (
          <IconWithLabel
            icon={['far', 'list-alt']}
            typographyProps={{ variant: 'helper' }}
            iconProps={{ color: 'coldgrey500' }}
          >
            {f({ id: 'layout_header.see_all_companies' })}
          </IconWithLabel>
        ),
      },
    ],
  ], [chooseIconColor, companies, f, onSelectingCompany]);

  const languagesMenu = useMemo(() => [
    languages.map((d) => ({
      ...d,
      onClick: () => {
        dispatch(userActions.setActiveLanguage(d.id));
        if (isAuthenticated) {
          // isto contorna o erro no back end nao reconhecer novas chaves
          const localeParser = {
            pt: 'pt-BR',
            'pt-BR': 'pt-BR',
            'pt-PT': 'pt-BR',

            es: 'es-MX',
            'es-ES': 'es-MX',
            'es-MX': 'es-MX',
          };

          dispatch(userActions.updateUserRequest({ user: { locale: localeParser[d.id] || d.id } }));
        }
      },
      label: (
        <Box display='flex'>
          <img alt={d.name} src={d.image} style={{ width: 28, height: 28 }} />
          <Typography ml='12px' variant='helper'>{d.name}</Typography>
        </Box>
      ),
    })),
  ], [dispatch, isAuthenticated]);

  const menu = useMemo(() => {
    let options = [
      {
        id: 'help',
        label: (
          <Link to={routes.support}>
            <IconWithLabel icon='question-circle' typographyProps={{ variant: 'helper' }}>
              {f({ id: 'layout_header.help' })}
            </IconWithLabel>
          </Link>
        ),
      },
    ];

    if (!['staging', 'production'].includes(process.env.REACT_APP_ENV)) {
      options = [...options, {
        id: 'demos',
        label: (
          <Link to={routes.demos}>
            <IconWithLabel icon='cog' typographyProps={{ variant: 'helper' }}>
              DEMOS DE COMPONENTES
            </IconWithLabel>
          </Link>
        ),
      }];
    }

    return options;
  }, [f, routes]);

  const userMenu = useMemo(() => [
    {
      id: 'my-profile',
      label: (
        <Link to={routes.myProfile}>
          <IconWithLabel icon='user-circle' typographyProps={{ variant: 'helper' }}>
            {f({ id: 'layout_header.options.my_profile' })}
          </IconWithLabel>
        </Link>
      ),
    },
    {
      id: 'logout',
      onClick: () => dispatch(authActions.logoutRequest()),
      label: (
        <IconWithLabel icon='sign-out-alt' typographyProps={{ variant: 'helper' }}>
          {f({ id: 'layout_header.sign_out' })}
        </IconWithLabel>
      ),
    },
  ], [dispatch, f, routes.myProfile]);

  const managerConfig = useMemo(() => [
    userIsOwner && {
      id: 'organization-profile',
      label: (
        <Link to={routes.manager.organizationProfile}>
          <IconWithLabel icon='industry-alt' typographyProps={{ variant: 'helper' }}>
            {f({ id: 'layout_header.options.organizacional_profile' })}
          </IconWithLabel>
        </Link>
      ),
    },
    {
      id: 'organization-respondents',
      label: (
        <Link to={routes.manager.respondents}>
          <IconWithLabel icon='chart-bar' typographyProps={{ variant: 'helper' }}>
            {f({ id: 'layout_header.options.respondents' })}
          </IconWithLabel>
        </Link>
      ),
    },
    {
      id: 'owners',
      label: (
        <Link to={routes.manager.managers}>
          <IconWithLabel icon='users' typographyProps={{ variant: 'helper' }}>
            {f({ id: 'layout_header.options.managers' })}
          </IconWithLabel>
        </Link>
      ),
    },
  ].filter((d) => d), [
    f,
    routes.manager.managers,
    routes.manager.organizationProfile,
    routes.manager.respondents,
    userIsOwner,
  ]);

  return {
    companiesMenu,
    managerConfig,
    languagesMenu,
    menu,
    userMenu,
  };
};

export default useHeaderMenuDropdown;
